import { yupResolver } from '@hookform/resolvers/yup'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { getTranslation } from '../../../../account/services/auth'
import { Topup, TopupPayload } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../../core/components/FormUploadPreview'
import { formatDateTime } from '../../../../core/constants/times'
import {
  getSettingsTopup,
  updateSettingsTopup,
} from '../../../../core/services/festival-settings'
import translate from '../../../../core/services/translate'
import { uploadImage } from '../../../../core/services/upload-image'
import { MessageBox } from '../../messages/MessageBox'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Card, Button, Checkbox, Input } from '@/components/ui'
import { InfoIcon } from 'lucide-react'

interface ITopupValues {
  topupEnabled: boolean
  autoTopupEnabled: boolean
  redeemTopupVouchersEnabled: boolean
  topupMyBalanceEnabled: boolean
  calculateBalancesEnabled: boolean
  refundEnabled: boolean
  refundStartDate?: string | null
  refundEndDate?: string | null
  refundCardTax?: number | null
  refundCardMinimumAmount?: number | null
  refundDonationEnabled: boolean
  donateBannerImageLink?: string | null
  refundDonationTax?: number | null
  refundDonationMinimumAmount?: number | null
  topupOnlineExpiryDate: string
  topupCampaignsExpiryDate: string
  festipaySyncEnabled: boolean
  festipayOnlineLimit?: number | null
  festipayRewardLimit?: number | null
  enableBalanceSync: boolean
  enableWalletSync: boolean
}

interface TopupModuleProps {
  endTime: number
  hasEditPermission: boolean
}

export const TopupModule: React.FC<TopupModuleProps> = ({
  endTime,
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()
  const language = getTranslation()

  const [topup, setTopup] = useState<Topup>()
  const [donateImg, setDonateImg] = useState<string>()
  const [initialImage, setInitialImage] = useState<string>()
  const [donateImgName, setDonateImgName] = useState<string>()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsTopup()
        if (resp) {
          setTopup(resp.data)
          setValue('topupEnabled', resp.data.topupEnabled, { shouldValidate: true })
          setValue('autoTopupEnabled', resp.data.autoTopupEnabled, { shouldValidate: true })
          setValue('redeemTopupVouchersEnabled', resp.data.redeemTopupVouchersEnabled, { shouldValidate: true })
          setValue('calculateBalancesEnabled', resp.data.calculateBalancesEnabled, { shouldValidate: true })
          setValue('topupMyBalanceEnabled', resp.data.topupMyBalanceEnabled, { shouldValidate: true })
          setValue('refundEnabled', resp.data.refundEnabled, { shouldValidate: true })
          setValue('enableBalanceSync', resp.data.enableBalanceSync, { shouldValidate: true })
          setValue('enableWalletSync', resp.data.enableWalletSync, { shouldValidate: true })
          if (resp.data.refundStartDate) {
            setValue('refundStartDate', formatDateTime(resp.data.refundStartDate), { shouldValidate: true })
          } else {
            resetField('refundStartDate')
          }
          if (resp.data.refundEndDate) {
            setValue('refundEndDate', formatDateTime(resp.data.refundEndDate), { shouldValidate: true })
          } else {
            resetField('refundEndDate')
          }
          setValue('refundCardTax', resp.data.refundCardTax, { shouldValidate: true })
          setValue('refundCardMinimumAmount', resp.data.refundCardMinimumAmount, { shouldValidate: true })
          setValue('refundDonationEnabled', resp.data.refundDonationEnabled, { shouldValidate: true })
          setValue('donateBannerImageLink', resp.data.donateBannerImageLink, { shouldValidate: true })
          if (resp.data.donateBannerImageLink) {
            setDonateImg(resp.data.donateBannerImageLink)
            setInitialImage(resp.data.donateBannerImageLink)
            const url = new URL(resp.data.donateBannerImageLink)
            const i = url?.pathname?.lastIndexOf('/') + 1
            const imageName = url?.pathname?.substring(i)
            setDonateImgName(imageName)
          }
          setValue('refundDonationTax', resp.data.refundDonationTax, { shouldValidate: true })
          setValue('refundDonationMinimumAmount', resp.data.refundDonationMinimumAmount, { shouldValidate: true })
          if (resp.data.topupOnlineExpiryDate) {
            setValue('topupOnlineExpiryDate', formatDateTime(resp.data.topupOnlineExpiryDate), { shouldValidate: true })
          } else {
            resetField('topupOnlineExpiryDate')
          }
          if (resp.data.topupCampaignsExpiryDate) {
            setValue('topupCampaignsExpiryDate', formatDateTime(resp.data.topupCampaignsExpiryDate), { shouldValidate: true })
          } else {
            resetField('topupCampaignsExpiryDate')
          }
          setValue('festipaySyncEnabled', resp.data.festipaySyncEnabled, { shouldValidate: true })
          setValue('festipayOnlineLimit', resp.data.festipayOnlineLimit, { shouldValidate: true })
          setValue('festipayRewardLimit', resp.data.festipayRewardLimit, { shouldValidate: true })
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape(
    {
      topupEnabled: yup.boolean().required(),
      redeemTopupVouchersEnabled: yup.boolean().required(),
      topupMyBalanceEnabled: yup.boolean().required(),
      calculateBalancesEnabled: yup.boolean().required(),
      refundEnabled: yup.boolean().required(),
      topupOnlineExpiryDate: yup.string().nonNullable().required(),
      topupCampaignsExpiryDate: yup.string().nonNullable().required(),
      refundStartDate: yup.string().when('refundEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
      refundEndDate: yup.string().when('refundEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
      refundCardTax: yup.number().when('refundEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) =>
          schema.nonNullable().transform((val) => (!isNaN(val) ? val : null)),
        otherwise: (schema) =>
          schema
            .nullable()
            .optional()
            .transform((val) => (!isNaN(val) ? val : null)),
      }),
      refundCardMinimumAmount: yup.number().when('refundEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) =>
          schema
            .nonNullable()
            .required()
            .transform((val) => (!isNaN(val) ? val : null)),
        otherwise: (schema) =>
          schema
            .nullable()
            .optional()
            .transform((val) => (!isNaN(val) ? val : null)),
      }),
      refundDonationEnabled: yup.boolean().required(),
      donateBannerImageLink: yup.string().when('refundDonationEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
      refundDonationTax: yup.number().when('refundDonationEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
      refundDonationMinimumAmount: yup.number().when('refundDonationEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
      autoTopupEnabled: yup.boolean().required(),
      festipaySyncEnabled: yup.boolean().required(),
      festipayOnlineLimit: yup.number().when('festipaySyncEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) =>
          schema
            .nullable()
            .optional()
            .transform((v) => (isNaN(v) ? 0 : v)),
      }),
      festipayRewardLimit: yup.number().when('festipaySyncEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) =>
          schema
            .nullable()
            .optional()
            .transform((v) => (isNaN(v) ? 0 : v)),
      }),
      enableBalanceSync: yup.boolean().required().default(false),
      enableWalletSync: yup.boolean().required().default(false),
    },
    [
      ['refundEnabled', 'refundStartDate'],
      ['refundEnabled', 'refundEndDate'],
      ['refundEnabled', 'refundCardTax'],
      ['refundEnabled', 'refundCardMinimumAmount'],
      ['refundDonationEnabled', 'donateBannerImageLink'],
      ['refundDonationEnabled', 'refundDonationTax'],
      ['refundDonationEnabled', 'refundDonationMinimumAmount'],
      ['festipaySyncEnabled', 'festipayOnlineLimit'],
      ['festipaySyncEnabled', 'festipayRewardLimit'],
    ],
  )

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    resetField,
    control,
    watch,
  } = useForm<ITopupValues>({
    mode: 'onChange',
    resolver: yupResolver<ITopupValues>(schema),
    defaultValues: {
      topupMyBalanceEnabled: false,
      calculateBalancesEnabled: false,
      refundEnabled: false,
      refundDonationEnabled: false,
      redeemTopupVouchersEnabled: false,
      topupEnabled: false,
      autoTopupEnabled: false,
      topupOnlineExpiryDate: undefined,
      topupCampaignsExpiryDate: undefined,
      refundCardMinimumAmount: undefined,
      refundCardTax: undefined,
      festipaySyncEnabled: false,
      festipayOnlineLimit: undefined,
      festipayRewardLimit: undefined,
      enableBalanceSync: false,
      enableWalletSync: false,
    },
  })

  const refundWatch = watch('refundEnabled')
  const donateWatch = watch('refundDonationEnabled')

  const submit = async (values: ITopupValues) => {
    try {
      const payload: TopupPayload = {
        topupEnabled: values.topupEnabled,
        autoTopupEnabled: values.autoTopupEnabled,
        redeemTopupVouchersEnabled: values.redeemTopupVouchersEnabled,
        topupMyBalanceEnabled: values.topupMyBalanceEnabled,
        calculateBalancesEnabled: values.calculateBalancesEnabled,
        refundEnabled: values.refundEnabled,
        refundStartDate:
          values.refundEnabled && values.refundStartDate
            ? new Date(values.refundStartDate).valueOf()
            : null,
        refundEndDate:
          values.refundEnabled && values.refundEndDate
            ? new Date(values.refundEndDate).valueOf()
            : null,
        refundCardTax: values.refundCardTax,
        refundCardMinimumAmount: values.refundCardMinimumAmount,
        refundDonationEnabled: values.refundDonationEnabled,
        refundDonationTax: values.refundDonationEnabled
          ? values.refundDonationTax
          : null,
        refundDonationMinimumAmount: values.refundDonationEnabled
          ? values.refundDonationMinimumAmount
          : null,
        topupOnlineExpiryDate: new Date(values.topupOnlineExpiryDate).valueOf(),
        topupCampaignsExpiryDate: new Date(
          values.topupCampaignsExpiryDate,
        ).valueOf(),
        festipaySyncEnabled: values.festipaySyncEnabled,
        festipayOnlineLimit: values.festipaySyncEnabled
          ? values.festipayOnlineLimit
          : null,
        festipayRewardLimit: values.festipaySyncEnabled
          ? values.festipayRewardLimit
          : null,
        enableBalanceSync: values.enableBalanceSync,
        enableWalletSync: values.enableWalletSync,
      }
      if (values.refundDonationEnabled && initialImage !== donateImg) {
        payload.donateBannerImageKey = values.donateBannerImageLink
      }
      const { status } = await updateSettingsTopup(payload)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (topup) {
      setValue('topupEnabled', topup.topupEnabled)
      setValue('autoTopupEnabled', topup.autoTopupEnabled)
      setValue('redeemTopupVouchersEnabled', topup.redeemTopupVouchersEnabled)
      setValue('topupMyBalanceEnabled', topup.topupMyBalanceEnabled)
      setValue('calculateBalancesEnabled', topup.calculateBalancesEnabled)
      setValue('refundEnabled', topup.refundEnabled)
      setValue('refundStartDate', formatDateTime(topup.refundStartDate))
      setValue('refundEndDate', formatDateTime(topup.refundEndDate))
      setValue('refundDonationEnabled', topup.refundDonationEnabled)
      setValue('refundCardTax', topup.refundCardTax)
      setValue('refundCardMinimumAmount', topup.refundCardMinimumAmount)
      setValue('donateBannerImageLink', topup.donateBannerImageLink)
      setValue('refundDonationTax', topup.refundDonationTax)
      setValue('refundDonationMinimumAmount', topup.refundDonationMinimumAmount)
      setValue(
        'topupOnlineExpiryDate',
        formatDateTime(topup.topupOnlineExpiryDate),
      )
      setValue(
        'topupCampaignsExpiryDate',
        formatDateTime(topup.topupCampaignsExpiryDate),
      )
      setValue('festipaySyncEnabled', topup.festipaySyncEnabled)
      setValue('festipayOnlineLimit', topup.festipayOnlineLimit)
      setValue('festipayRewardLimit', topup.festipayRewardLimit)
      setValue('enableBalanceSync', topup.enableBalanceSync)
      setValue('enableWalletSync', topup.enableWalletSync)
    }
  }

  return (
    <>
      <Card className="bg-gradient-4 p-7.5 border border-monochrome-120">
        <h3 className="text-title-xs text-monochrome-1 mb-4">Top Up</h3>
        <form
          id="settings-topup-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-[28px]"
        >
          <div className="space-y-4">
            <FormGroup
              label="Enable Top Up"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'topupEnabled',
              }}
            >
              <Controller
                name="topupEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="topupEnabled"
                    isInvalid={!!errors?.topupEnabled?.message}
                    onCheckedChange={(checked) => {
                      field.onChange(Boolean(checked))
                    }}
                    disabled={!hasEditPermission}
                    checked={Boolean(field.value)}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Reedeem Topup Vouchers"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'redeemTopupVouchersEnabled',
              }}
            >
              <Controller
                name="redeemTopupVouchersEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="redeemTopupVouchersEnabled"
                    isInvalid={!!errors?.redeemTopupVouchersEnabled?.message}
                    onCheckedChange={(checked) => {
                      field.onChange(Boolean(checked))
                    }}
                    disabled={!hasEditPermission}
                    checked={Boolean(field.value)}
                  />
                )}
              />
            </FormGroup>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormGroup label="Top Up with Card Expiration Date">
              <DateTimePicker
                className="h-[44px] rounded-2xl"
                value={
                  watch('topupOnlineExpiryDate')
                    ? new Date(watch('topupOnlineExpiryDate') as string)
                    : null
                }
                onChange={(date) => {
                  setValue(
                    'topupOnlineExpiryDate',
                    date ? formatDateTime(date.toISOString()) : '',
                  )
                }}
                placeholder="Enter Refund From Date"
                hasError={!!errors?.topupOnlineExpiryDate?.message}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Top Up from Vouchers, Campaigns, Rewards">
              <DateTimePicker
                className="h-[44px] rounded-2xl"
                value={
                  watch('topupCampaignsExpiryDate')
                    ? new Date(watch('topupCampaignsExpiryDate') as string)
                    : null
                }
                onChange={(date) => {
                  setValue(
                    'topupCampaignsExpiryDate',
                    date ? formatDateTime(date.toISOString()) : '',
                  )
                }}
                placeholder="Enter Refund From Date"
                hasError={!!errors?.topupCampaignsExpiryDate?.message}
                disabled={!hasEditPermission}
              />
            </FormGroup>
          </div>
          <div className="grid grid-cols-4 gap-3 divide-x divide-monochrome-120">
            <FormGroup
              label="Enable Top Up My Balance"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'topupMyBalanceEnabled',
              }}
            >
              <Controller
                name="topupMyBalanceEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="topupMyBalanceEnabled"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.topupMyBalanceEnabled?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Enable Calculating Balances"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'calculateBalancesEnabled',
              }}
            >
              <Controller
                name="calculateBalancesEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="calculateBalancesEnabled"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.calculateBalancesEnabled?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Enable Auto Top Up"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'autoTopupEnabled',
              }}
            >
              <Controller
                name="autoTopupEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="autoTopupEnabled"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.autoTopupEnabled?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Enable Refund"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'refundEnabled',
              }}
            >
              <Controller
                name="refundEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="refundEnabled"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.refundEnabled?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
          </div>
          {refundWatch && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-[28px]">
              <FormGroup
                label="Enable Refund From"
                tooltip={translate(
                  language.value,
                  'settings.general.enableRefundFromTooltip',
                )}
              >
                <DateTimePicker
                  className="h-[44px] rounded-2xl"
                  value={
                    watch('refundStartDate')
                      ? new Date(watch('refundStartDate') as string)
                      : null
                  }
                  onChange={(date) => {
                    setValue(
                      'refundStartDate',
                      date ? formatDateTime(date.toISOString()) : null,
                    )
                  }}
                  placeholder="Enter Refund From Date"
                  hasError={!!errors?.refundStartDate?.message}
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup
                label="Enable Refund Until"
                tooltip={translate(
                  language.value,
                  'settings.general.enableRefundUntilTooltip',
                )}
              >
                <DateTimePicker
                  className="h-[44px] rounded-2xl"
                  value={
                    watch('refundEndDate')
                      ? new Date(watch('refundEndDate') as string)
                      : null
                  }
                  onChange={(date) => {
                    setValue(
                      'refundEndDate',
                      date ? formatDateTime(date.toISOString()) : null,
                    )
                  }}
                  placeholder="Enter Refund Until Date"
                  hasError={!!errors?.refundEndDate?.message}
                  disabled={!refundWatch || !hasEditPermission}
                />
              </FormGroup>

              <FormGroup label="Refund to Card Tax">
                <Input
                  min={0}
                  isInvalid={!!errors?.refundCardTax?.message}
                  {...register('refundCardTax')}
                  type="number"
                  placeholder="Enter Refund Tax"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="Minimum Amount to Refund to Card">
                <Input
                  min={0}
                  isInvalid={!!errors?.refundCardMinimumAmount?.message}
                  {...register('refundCardMinimumAmount')}
                  type="number"
                  placeholder="Enter Refund Minimum Amount"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
            </div>
          )}
          <FormGroup
            label="Enable Donate"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'refundDonationEnabled',
            }}
          >
            <Controller
              name="refundDonationEnabled"
              control={control}
              render={({ field }) => (
                <Checkbox
                  ref={field.ref}
                  id="refundDonationEnabled"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  isInvalid={!!errors?.refundDonationEnabled?.message}
                  disabled={!hasEditPermission}
                />
              )}
            />
          </FormGroup>
          {donateWatch && (
            <>
              <div className="flex justify-between gap-3">
                <FormGroup label="Donate Tax">
                  <Input
                    min={0}
                    isInvalid={!!errors?.refundDonationTax?.message}
                    {...register('refundDonationTax')}
                    type="number"
                    placeholder="Enter Donation Tax"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup label="Minimum Amount to Donate">
                  <Input
                    min={0}
                    isInvalid={!!errors?.refundDonationMinimumAmount?.message}
                    {...register('refundDonationMinimumAmount')}
                    type="number"
                    placeholder="Enter Donation Minimum Amount"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              </div>
              <FormGroup label="Donate Banner Image (650X190)">
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.donateBannerImageLink?.message ||
                        invalidPhoto ||
                        isErrorRatio
                      }
                      name="photo"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          setInvalidPhoto(false)
                          setIsErrorRatio(false)
                          if (file.size > 1_000_000) {
                            setInvalidPhoto(true)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const photo = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'SETTINGS_TOP_UP_DONATE_BANNER',
                            false,
                          )
                          if (photo) {
                            setValue(`donateBannerImageLink`, photo.photoKey, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDonateImg(photo.photoUrl)
                            setDonateImgName(file.name)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  imageName={donateImgName}
                  isUploading={isUploading}
                  url={donateImg}
                  onRemove={() => {
                    setValue('donateBannerImageLink', '')
                    setDonateImg('')
                    setDonateImgName('')
                  }}
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                />
              </FormGroup>
              {isErrorRatio && (
                <div className="p-3 flex flex-row items-center bg-monochrome-3-60 border border-monochrome-120 w-full">
                  <InfoIcon className="me-2 text-accent-3" />
                  <span className="w-full text-accent-3">
                    The image uploaded has a different aspect ration than
                    required. Please try again.
                  </span>
                </div>
              )}
            </>
          )}
          <div className="grid grid-cols-4 gap-3 divide-x divide-monochrome-120">
            <FormGroup
              label="Sync Topups to Festipay"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'festipaySyncEnabled',
              }}
            >
              <Controller
                name="festipaySyncEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="festipaySyncEnabled"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.festipaySyncEnabled?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Enable Balance Sync"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'enableBalanceSync',
              }}
            >
              <Controller
                name="enableBalanceSync"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="enableBalanceSync"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.enableBalanceSync?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Enable Wallet Sync"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'enableWalletSync',
              }}
            >
              <Controller
                name="enableWalletSync"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    id="enableWalletSync"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.enableWalletSync?.message}
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
          </div>
          <div className="p-3 bg-monochrome-3-60 rounded-2xl border border-monochrome-120 w-full">
            <h5 className="mb-4 text-title-xs text-monochrome-1">
              Limits on Pending to Sync Topups
            </h5>
            <div className="flex justify-between gap-3 w-full">
              <FormGroup label="Online Deposit">
                <Input
                  min={0}
                  isInvalid={!!errors?.festipayOnlineLimit?.message}
                  {...register('festipayOnlineLimit')}
                  type="number"
                  placeholder="Enter Online Deposit"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="Reward">
                <Input
                  min={0}
                  isInvalid={!!errors?.festipayRewardLimit?.message}
                  {...register('festipayRewardLimit')}
                  type="number"
                  placeholder="Enter Reward Limit"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-topup-form"
            type="submit"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
